(() => {
  const promotionSelector = 'product-promotion[data-form-id]';

  const getButtonLabelElement = (button) => {
    if (!button) return null;
    return button.getAttribute('is') === 'custom-button' ? button.firstElementChild || button : button;
  };

  const getButtonLabel = (button) => getButtonLabelElement(button)?.textContent.trim() || '';

  const setButtonLabel = (button, label) => {
    const labelElement = getButtonLabelElement(button);
    if (labelElement) labelElement.textContent = label;
  };

  const getControlValue = (form, name) => {
    const control = form?.elements?.namedItem(name);
    if (!control) return '';
    return typeof control.value === 'string' ? control.value : control[0]?.value || '';
  };

  const initializePromotion = (promotion) => {
    if (promotion.dataset.initialized === 'true') return;
    promotion.dataset.initialized = 'true';

    const form = document.getElementById(promotion.dataset.formId);
    const productInfo = promotion.closest('.product-info');
    const wrapper = productInfo?.querySelector('.yg-product-form__buy-buttons--main');
    const cardButtons = wrapper?.querySelector('.product-info__buy-buttons .yg-product-card__buttons');
    const addButton = cardButtons?.querySelector('.yg-add-to-cart');
    const buyButton = cardButtons?.querySelector('.yg-buy-it-now');
    const nativeSubmitButton = form?.querySelector('button[type="submit"]');
    const giftToggle = promotion.querySelector('[data-gift-toggle]');
    const selectionLabel = promotion.querySelector('[data-selection-label]');
    const unselectedLabel = selectionLabel?.textContent.trim() || 'Get a Free Gift';
    const selectedBuyLabel = promotion.dataset.buyButtonLabel?.trim() || 'Buy with Free Gift';
    const errorElement = promotion.querySelector('[data-promotion-error]');
    const giftVariantId = promotion.dataset.giftVariantId;
    const eligibleVariantIds = new Set(
      (promotion.dataset.eligibleVariantIds || '')
        .split(',')
        .filter(Boolean)
    );
    const expiration = Number(promotion.dataset.expiresAt || 0);
    const requestTimeout = 15000;
    const lockableRegions = [
      form,
      ...(productInfo
        ? [...productInfo.querySelectorAll('.product-info__variant-picker, .product-info__quantity-selector')]
        : [])
    ].filter(Boolean);

    if (!form || !giftVariantId || !giftToggle) return;

    let baseAddLabel = getButtonLabel(addButton) || 'Add to Cart';
    let baseBuyLabel = getButtonLabel(buyButton) || 'Buy Now';
    const managedButtons = [...new Set([giftToggle, addButton, buyButton, nativeSubmitButton].filter(Boolean))];
    let disabledBeforeBusy = new Map();
    let inertBeforeBusy = new Map();
    let selected = false;
    let busy = false;
    let stateVersion = 0;
    let busyStateVersion = 0;
    let countdownTimer;
    let expired = false;

    const setError = (message) => {
      if (!errorElement) return;
      errorElement.textContent = message || 'We could not add the free gift. Please try again.';
      errorElement.hidden = !message;
    };

    const setBusy = (isBusy) => {
      busy = isBusy;
      const stateChanged = stateVersion !== busyStateVersion;

      if (isBusy) {
        busyStateVersion = stateVersion;
        disabledBeforeBusy = new Map(managedButtons.map((button) => [button, button.disabled]));
        inertBeforeBusy = new Map(lockableRegions.map((region) => [region, region.hasAttribute('inert')]));
        lockableRegions.forEach((region) => region.setAttribute('inert', ''));
        managedButtons.forEach((button) => {
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
        });
        return;
      }

      managedButtons.forEach((button) => {
        if (button === giftToggle || !stateChanged) {
          button.disabled = disabledBeforeBusy.get(button) === true;
        }
        button.removeAttribute('aria-busy');
      });
      lockableRegions.forEach((region) => {
        if (inertBeforeBusy.get(region) === true) {
          region.setAttribute('inert', '');
        } else {
          region.removeAttribute('inert');
        }
      });
      disabledBeforeBusy.clear();
      inertBeforeBusy.clear();
      giftToggle.disabled = expired || isExcludedVariant();
    };

    const fetchJsonWithTimeout = async (url, options = {}, onResponse) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), requestTimeout);
      try {
        const response = await fetch(url, { ...options, signal: controller.signal });
        if (onResponse) onResponse(response);
        let responseJson = {};
        try {
          responseJson = await response.json();
        } catch (error) {
          if (response.ok) throw error;
        }
        return { response, responseJson };
      } finally {
        clearTimeout(timeoutId);
      }
    };

    const syncLabels = () => {
      setButtonLabel(addButton, selected ? 'Add Both to Cart' : baseAddLabel);
      setButtonLabel(buyButton, selected ? selectedBuyLabel : baseBuyLabel);
    };

    const refreshBaseLabels = () => {
      const currentAddLabel = getButtonLabel(addButton);
      const currentBuyLabel = getButtonLabel(buyButton);

      if (currentAddLabel && currentAddLabel !== 'Add Both to Cart') baseAddLabel = currentAddLabel;
      if (currentBuyLabel && (!selected || isPreorderVariant() || currentBuyLabel !== selectedBuyLabel)) baseBuyLabel = currentBuyLabel;
      syncLabels();
    };

    const setSelected = (isSelected) => {
      selected = isSelected;
      promotion.classList.toggle('is-selected', selected);
      giftToggle.checked = selected;
      if (selectionLabel) selectionLabel.textContent = selected ? 'Auto-Applied in Cart' : unselectedLabel;
      syncLabels();
    };

    const isPreorderVariant = () => cardButtons?.classList.contains('is-preorder') === true;
    const isSoldOutVariant = () => cardButtons?.classList.contains('is-preorder-blocked') === true;
    const isEligibleVariant = () => eligibleVariantIds.size === 0
      || eligibleVariantIds.has(String(getControlValue(form, 'id')));
    const isExcludedVariant = () => isPreorderVariant() || isSoldOutVariant() || !isEligibleVariant();

    const syncPromotionVisibility = () => {
      if (!promotion.isConnected) return;
      const excluded = isExcludedVariant();
      giftToggle.disabled = expired || excluded;
      if (excluded) {
        if (selected) setSelected(false);
        promotion.hidden = true;
        return;
      }
      promotion.hidden = expired;
      giftToggle.disabled = expired;
    };

    const hideExpired = () => {
      if (!promotion.isConnected) {
        if (countdownTimer) clearInterval(countdownTimer);
        return true;
      }
      if (!expiration || Date.now() < expiration * 1000) {
        syncPromotionVisibility();
        return false;
      }
      if (countdownTimer) clearInterval(countdownTimer);
      expired = true;
      setSelected(false);
      promotion.hidden = true;
      return true;
    };

    const updateCountdown = () => {
      if (hideExpired()) return;
      const remaining = Math.max(0, Math.floor(expiration - Date.now() / 1000));
      const days = Math.floor(remaining / 86400);
      const hours = Math.floor((remaining % 86400) / 3600);
      const minutes = Math.floor((remaining % 3600) / 60);
      const seconds = remaining % 60;
      const countdownElement = promotion.querySelector('[data-countdown]');
      const dayElement = promotion.querySelector('[data-countdown-day]');
      const daysElement = promotion.querySelector('[data-countdown-days]');
      const dayLabelElement = promotion.querySelector('[data-countdown-day-label]');
      const hoursElement = promotion.querySelector('[data-countdown-hours]');
      const minutesElement = promotion.querySelector('[data-countdown-minutes]');
      const secondsElement = promotion.querySelector('[data-countdown-seconds]');

      if (dayElement) dayElement.hidden = days < 1;
      if (daysElement) daysElement.textContent = String(days);
      if (dayLabelElement) dayLabelElement.textContent = 'd';
      if (hoursElement) hoursElement.textContent = String(hours).padStart(2, '0');
      if (minutesElement) minutesElement.textContent = String(minutes).padStart(2, '0');
      if (secondsElement) secondsElement.textContent = String(seconds).padStart(2, '0');
      if (countdownElement) {
        const dayLabel = days > 0 ? `${days} ${days === 1 ? 'day' : 'days'}, ` : '';
        countdownElement.setAttribute('aria-label', `Offer ends in ${dayLabel}${hours} hours, ${minutes} minutes, ${seconds} seconds`);
      }
    };

    const readPurchaseState = () => {
      const formData = new FormData(form);
      const mainVariantId = getControlValue(form, 'id');
      const quantityValue = getControlValue(form, 'quantity');
      const quantity = Math.max(1, Number.parseInt(quantityValue, 10) || 1);
      const sellingPlan = getControlValue(form, 'selling_plan') || String(formData.get('selling_plan') || '');
      const properties = {};
      const propertyEntries = [];

      for (const [name, value] of formData.entries()) {
        if (name.startsWith('properties[') && name.endsWith(']')) {
          const propertyName = name.slice(11, -1);
          const propertyValue = String(value);
          properties[propertyName] = propertyValue;
          propertyEntries.push([propertyName, propertyValue]);
        }
      }
      propertyEntries.sort(([nameA], [nameB]) => nameA.localeCompare(nameB));

      return {
        mainVariantId,
        quantity,
        sellingPlan,
        properties,
        fingerprint: JSON.stringify([String(mainVariantId), quantity, sellingPlan, propertyEntries])
      };
    };

    const readBundleItems = () => {
      const purchaseState = readPurchaseState();
      if (!purchaseState.mainVariantId) throw new Error('Select an available product option before adding the gift.');
      if (addButton?.disabled || nativeSubmitButton?.disabled) throw new Error('This product is currently unavailable.');

      const mainItem = { id: String(purchaseState.mainVariantId), quantity: purchaseState.quantity };
      if (purchaseState.sellingPlan) mainItem.selling_plan = purchaseState.sellingPlan;
      if (Object.keys(purchaseState.properties).length) mainItem.properties = purchaseState.properties;

      return {
        items: [mainItem, { id: String(giftVariantId), quantity: purchaseState.quantity }],
        fingerprint: purchaseState.fingerprint
      };
    };

    const dispatchBundleError = (message) => {
      const detail = { error: message };
      form.dispatchEvent(new CustomEvent('cart:error', { bubbles: true, detail }));
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    };

    const addBundle = async (checkoutAfterAdd) => {
      if (busy || promotion.hidden || expired || isExcludedVariant()) return;
      let items;
      let purchaseFingerprint;
      let addResponseReceived = false;
      let bundleCommitted = false;

      try {
        const bundle = readBundleItems();
        items = bundle.items;
        purchaseFingerprint = bundle.fingerprint;
      } catch (error) {
        setError(error.message);
        dispatchBundleError(error.message);
        return;
      }

      const routesRoot = window.Shopify?.routes?.root || '/';
      const sectionsToBundle = ['variant-added'];
      setError('');
      setBusy(true);
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      if (purchaseFingerprint !== readPurchaseState().fingerprint) {
        const message = 'Your product selection changed. Please review it and try again.';
        setError(message);
        setBusy(false);
        return;
      }

      try {
        const { response, responseJson } = await fetchJsonWithTimeout(`${routesRoot}cart/add.js`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items, sections: sectionsToBundle.join(','), sections_url: `${routesRoot}variants/${items[0].id}` })
        }, (receivedResponse) => {
          addResponseReceived = true;
          bundleCommitted = receivedResponse.ok;
        });

        if (!response.ok) {
          throw new Error(responseJson.description || responseJson.message || 'We could not add the free gift.');
        }

        if (purchaseFingerprint !== readPurchaseState().fingerprint) {
          window.location.assign(`${routesRoot}cart`);
          return;
        }

        if (checkoutAfterAdd) {
          window.location.assign(`${routesRoot}checkout`);
          return;
        }

        const settings = window.themeVariables?.settings;
        if (settings?.cartType === 'page' || settings?.pageType === 'cart') {
          window.location.assign(`${routesRoot}cart`);
          return;
        }

        const { response: cartResponse, responseJson: cartContent } = await fetchJsonWithTimeout(`${routesRoot}cart.js`, { headers: { 'Accept': 'application/json' } });
        if (!cartResponse.ok) throw new Error('The cart was updated, but the cart display could not refresh.');
        cartContent.sections = responseJson.sections || {};
        const addedItems = Array.isArray(responseJson.items) ? responseJson.items : [responseJson];

        promotion.dispatchEvent(new CustomEvent('variant:add', {
          bubbles: true,
          detail: { items: addedItems, cart: cartContent }
        }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartContent }
        }));
      } catch (error) {
        if (bundleCommitted || !addResponseReceived) {
          setError('');
          window.location.assign(`${routesRoot}cart`);
          return;
        }
        const message = error?.name === 'AbortError'
          ? 'The request timed out. Please try again.'
          : error?.message || 'We could not add the free gift. Please try again.';
        setError(message);
        dispatchBundleError(message);
      } finally {
        setBusy(false);
        syncLabels();
      }
    };

    giftToggle.addEventListener('change', () => {
      if (busy || expired || isExcludedVariant()) {
        giftToggle.checked = selected;
        return;
      }
      setSelected(giftToggle.checked);
    });

    promotion.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.target.closest('a, input, label, button')) return;
      if (busy || expired || isExcludedVariant()) return;
      setSelected(!selected);
    });

    const interceptSelectedPurchase = (event, checkoutAfterAdd) => {
      if (!selected) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy || promotion.hidden || expired || isExcludedVariant()) return;
      void addBundle(checkoutAfterAdd);
    };

    addButton?.addEventListener('click', (event) => interceptSelectedPurchase(event, false), true);
    buyButton?.addEventListener('click', (event) => interceptSelectedPurchase(event, true), true);
    form.addEventListener('submit', (event) => interceptSelectedPurchase(event, false), true);
    form.addEventListener('variant:change', () => {
      stateVersion += 1;
      refreshBaseLabels();
      syncPromotionVisibility();
      setTimeout(() => {
        refreshBaseLabels();
        syncPromotionVisibility();
      }, 0);
    });

    setSelected(false);
    syncPromotionVisibility();
    if (expiration && promotion.dataset.showCountdown === 'true') {
      updateCountdown();
      if (!expired && promotion.isConnected) countdownTimer = setInterval(updateCountdown, 1000);
    } else if (expiration) {
      hideExpired();
      if (!expired && promotion.isConnected) countdownTimer = setInterval(hideExpired, 30000);
    }
  };

  const initializePromotions = () => {
    document.querySelectorAll(promotionSelector).forEach(initializePromotion);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializePromotions, { once: true });
  } else {
    initializePromotions();
  }
})();
